/**
 * Align a fragment target again while the page is still settling.
 *
 * A reloaded or pasted `#fragment` URL is scrolled by the browser before the
 * page is final: homepage sections animate in, fonts swap and images above the
 * target grow. The browser never corrects that afterwards, so an alignment
 * measured on the first frame can leave the target under the sticky header.
 *
 * Aligning once cannot fix a page that keeps moving, and aligning on a timer
 * guesses at how long it moves. So the caller reports where the target sits in
 * the document after each alignment, and this helper aligns again whenever that
 * position changes — a layout shift is the only reason to correct.
 *
 * That distinction matters. A changed scroll position is not a layout shift: it
 * is the person scrolling, a router reset or a test step, and the page must be
 * left where it was put. Only movement of the target itself is corrected, which
 * is why this cannot drag the viewport back after someone scrolls away.
 *
 * Corrections are instant, so a settling scroll lands where it measures, and
 * the runs are bounded: a quiet stretch ends them, and a frame cap stops a page
 * that never stops moving.
 *
 * Two more rules keep the helper from writing to the viewport it does not own.
 * A caller that finds its target already at rest must not scroll at all, so a
 * settled page produces no scroll calls and no scroll events. And the helper
 * belongs to the page it started on: once the pathname changes, the outgoing
 * page must not be scrolled while the destination renders.
 */
export type FragmentAlign = (options: { instant: boolean }) => number | void;

/** Frames without the target moving that count as settled (~300ms at 60fps). */
const QUIET_FRAMES = 18;
/** Hard stop for a page that never stops moving. */
const MAX_FRAMES = 900;
/** Movement below this many pixels is rounding, not a layout shift. */
const MOVEMENT_TOLERANCE = 1;
/** Pixels of drift that still count as "we are where we put ourselves". */
const OWN_SCROLL_TOLERANCE = 2;

export function alignWhenLayoutSettles(align: FragmentAlign): () => void {
  // The page this alignment belongs to. A route change while the loop is still
  // running means the destination owns the viewport now.
  const pathname = window.location.pathname;
  let cancelled = false;
  let frame = 0;
  let frames = 0;
  let quietFrames = 0;
  let lastPosition: number | null = null;
  let expectedScrollY: number | null = null;
  let loaded = document.readyState === 'complete';

  const stop = () => {
    if (cancelled) return;
    cancelled = true;
    window.cancelAnimationFrame(frame);
    window.removeEventListener('load', onSettled);
    window.removeEventListener('wheel', stop);
    window.removeEventListener('touchstart', stop);
    window.removeEventListener('keydown', stop);
  };

  const run = () => {
    if (cancelled) return;

    // A different route means this page is being left: scrolling it now would
    // target the outgoing content while the destination renders.
    if (window.location.pathname !== pathname) return;

    // Once the document is loaded, a scroll we did not apply is someone else's
    // — the person, a router reset, a test step — and the page must stay where
    // they put it. Before `load` the browser may still restore the fragment.
    if (loaded && expectedScrollY !== null && Math.abs(window.scrollY - expectedScrollY) > OWN_SCROLL_TOLERANCE) {
      return;
    }

    const position = align({ instant: true });
    expectedScrollY = window.scrollY;
    frames += 1;
    // No target to align to: there is nothing to wait for.
    if (typeof position !== 'number') return;

    const moved = lastPosition === null || Math.abs(position - lastPosition) > MOVEMENT_TOLERANCE;
    lastPosition = position;
    quietFrames = moved ? 0 : quietFrames + 1;

    if (loaded && quietFrames >= QUIET_FRAMES) return;
    if (frames >= MAX_FRAMES) return;
    frame = window.requestAnimationFrame(run);
  };

  // `load` and fonts change layout after the first frame, so both restart a
  // settling run and force one fresh alignment.
  const onSettled = () => {
    if (cancelled) return;
    loaded = true;
    lastPosition = null;
    expectedScrollY = null;
    quietFrames = 0;
    window.cancelAnimationFrame(frame);
    frame = window.requestAnimationFrame(run);
  };

  frame = window.requestAnimationFrame(run);
  window.addEventListener('load', onSettled, { once: true });
  const fonts = document.fonts;
  if (fonts) void fonts.ready.then(onSettled).catch(() => {});

  window.addEventListener('wheel', stop, { passive: true });
  window.addEventListener('touchstart', stop, { passive: true });
  window.addEventListener('keydown', stop);

  return stop;
}

/**
 * Scroll to the contact section and report where it sits in the document, so
 * the caller can keep aligning while the page settles.
 *
 * Nothing is scrolled when the section is already at rest under the header: a
 * redundant scroll call would fire scroll events and fight whatever else owns
 * the viewport, and there is nothing to correct.
 *
 * @param options.instant Skip the animation. Defaults to smooth, except when
 *   the person prefers reduced motion.
 */
export function scrollToContact(options: { instant?: boolean } = {}): number {
  const section = document.getElementById('contact');
  if (!section) return 0;
  const offset = section.getBoundingClientRect().top - 80;
  // A sub-pixel residual is rounding, not a misalignment: writing for it would
  // scroll every frame and never converge. The target is rounded so the section
  // always lands on a whole pixel either side of the header offset.
  if (Math.abs(offset) > 1) {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({
      top: Math.round(section.getBoundingClientRect().top + window.scrollY - 80),
      behavior: options.instant || reducedMotion ? 'instant' : 'smooth',
    });
  }
  if (document.activeElement !== section) {
    section.tabIndex = -1;
    section.focus({ preventScroll: true });
  }
  return section.getBoundingClientRect().top + window.scrollY;
}
